import { LoadingContainer } from '@/components/ui';
import { useDashboardAsesores, useEmpresasAdmin, useIndicadoresResumen } from '@/hooks';
import {
  useAsignarIndicador,
  useCrearIndicadorBase,
  useEliminarIndicadorBase,
  useEliminarIndicadorEmpresa,
  useHistorialIndicadores,
  useIndicadoresBaseSysadmin,
  useIndicadoresByEmpresa,
} from '@/hooks/useIndicadores';
import type { IndicadorEmpresa } from '@/services/indicadores.service';
import type { IndicadorBaseSysadmin } from '@/services/sysadmin.service';
import { sysadminService } from '@/services/sysadmin.service';
import {
  Activity,
  AlertTriangle,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Pencil,
  Plus,
  Search,
  Trash2,
  TrendingUp,
  X,
} from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

function formatValor(v: number | null | undefined, tipo: string, compact = false): string {
  if (v === null || v === undefined || isNaN(v)) return '—';
  if (tipo === 'MONEDA') {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
      notation: compact ? 'compact' : 'standard',
    }).format(v);
  }
  const formattedNum = Number.isFinite(v) ? parseFloat(v.toFixed(2)).toString() : '—';
  if (tipo === 'NUMERO') {
    return formattedNum;
  }
  return `${formattedNum}%`;
}

function formatFrecuencia(frec: string): string {
  if (!frec) return '';
  const lower = frec.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// ─── Types ───────────────────────────────────────────────

type TabId = 'resumen' | 'gestion' | 'asignaciones';

interface FormState {
  nombre: string;
  formula: string;
  frecuencia: 'MENSUAL' | 'TRIMESTRAL' | 'SEMESTRAL' | 'ANUAL';
  fuenteDatos: string;
  meta: string;
  tipoResultado: 'PORCENTAJE' | 'NUMERO' | 'MONEDA';
  variables: { nombre: string; orden: number }[];
}

// ─── Constants ───────────────────────────────────────────

const TABS: { id: TabId; label: string }[] = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'gestion', label: 'Gestión' },
  { id: 'asignaciones', label: 'Asignaciones' },
];

const EMPTY_FORM: FormState = {
  nombre: '',
  formula: '',
  frecuencia: 'MENSUAL',
  fuenteDatos: '',
  meta: '',
  tipoResultado: 'PORCENTAJE',
  variables: [{ nombre: '', orden: 1 }],
};

// ─── Shared input/button styles ──────────────────────────

const inputCls =
  'border border-(--admin-border) rounded-lg px-3 py-2 text-[13px] bg-(--admin-surface) text-(--admin-text) w-full focus:outline-none focus:ring-1 focus:ring-(--admin-text)';

const btnPrimary = 'px-3 py-1.5 text-[12px] font-medium rounded-lg bg-(--admin-text) text-white hover:opacity-90 transition-opacity';

const btnSecondary =
  'px-3 py-1.5 text-[12px] font-medium rounded-lg border border-(--admin-border) text-(--admin-text2) hover:bg-(--admin-surface2) transition-colors';

const btnDanger = 'px-3 py-1.5 text-[12px] font-medium rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors';

// ─── ResumenTab ──────────────────────────────────────────

function ResumenTab() {
  const { data, isLoading, error } = useIndicadoresResumen();

  if (isLoading) {
    return (
      <div className="admin-card min-h-100 flex items-center justify-center">
        <LoadingContainer text="Cargando resumen de indicadores..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-card p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
          <AlertTriangle className="w-7 h-7 text-red-500" />
        </div>
        <h3 className="text-[15px] font-semibold text-red-900 mb-1">Error al cargar datos</h3>
        <p className="text-[13px] text-red-600 max-w-md mx-auto">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const { resumen } = data;

  return (
    <>
      {/* Metrics */}
      <div className="admin-metrics-row">
        <div className="admin-metric-card">
          <div className="text-[11px] text-(--admin-text3) font-medium uppercase tracking-wider mb-1.5">Total indicadores</div>
          <div className="text-[26px] font-semibold tracking-tight">{resumen.totalIndicadores}</div>
          <div className="text-[11px] text-(--admin-text3) mt-1">Indicadores base configurados</div>
        </div>
        <div className="admin-metric-card">
          <div className="text-[11px] text-(--admin-text3) font-medium uppercase tracking-wider mb-1.5">Cumplimiento global</div>
          <div className="text-[26px] font-semibold tracking-tight text-(--admin-primary)">{Math.round(resumen.promedioCumplimiento)}%</div>
          <div className="text-[11px] text-(--admin-text3) mt-1">Promedio de {resumen.totalEmpresas} empresas</div>
        </div>
        <div className="admin-metric-card">
          <div className="text-[11px] text-(--admin-text3) font-medium uppercase tracking-wider mb-1.5">Empresas cumplen</div>
          <div className="text-[26px] font-semibold tracking-tight text-green-600">{resumen.empresasCumplen}</div>
          <div className="text-[11px] text-(--admin-text3) mt-1">Status saludable (&ge;80%)</div>
        </div>
        <div className="admin-metric-card">
          <div className="text-[11px] text-(--admin-text3) font-medium uppercase tracking-wider mb-1.5">Peor indicador</div>
          <div className="text-[13px] font-bold text-red-600 leading-tight mt-2 uppercase truncate max-w-37.5">
            {resumen.peorIndicador || '—'}
          </div>
          <div className="text-[11px] text-red-500/80 mt-1 font-medium">Indicador con mayor desviación</div>
        </div>
      </div>

      <div className="admin-two-col">
        {/* Top Empresas */}
        <div className="admin-card">
          <div className="admin-card-header border-b border-(--admin-border) px-4 py-3">
            <TrendingUp className="w-4 h-4 text-(--admin-text3)" />
            <div className="text-[13px] font-semibold text-(--admin-text)">Top empresas cumplimiento</div>
          </div>
          <div className="overflow-x-auto min-h-75">
            <table className="admin-table w-full">
              <thead>
                <tr className="border-b border-(--admin-border-subtle)">
                  <th className="text-left px-4 py-3 text-[11px] text-(--admin-text3) uppercase tracking-wider font-semibold">Empresa</th>
                  <th className="text-right px-4 py-3 text-[11px] text-(--admin-text3) uppercase tracking-wider font-semibold">Cump.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-(--admin-border-subtle)">
                {data.topEmpresas.map((e) => (
                  <tr key={e.nitId} className="hover:bg-(--admin-surface2) transition-colors text-[13px]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded bg-(--admin-surface2) flex items-center justify-center text-(--admin-text3)">
                          <Building2 className="w-3.5 h-3.5" />
                        </div>
                        <span className="font-medium text-(--admin-text)">{e.nombre}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold bg-green-50 text-green-700 border border-green-200/50">
                        {Math.round(e.porcentajeCumplimiento)}%
                      </span>
                    </td>
                  </tr>
                ))}
                {data.topEmpresas.length === 0 && (
                  <tr>
                    <td colSpan={2} className="p-12 text-center text-(--admin-text3)">
                      No hay datos de cumplimiento todavía
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Alertas */}
        <div className="admin-card border-red-100 shadow-sm shadow-red-50/50">
          <div className="admin-card-header border-b border-red-50 px-4 py-3 bg-red-50/30">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <div className="text-[13px] font-semibold text-red-900">Empresas con Alertas Críticas</div>
          </div>
          <div className="overflow-x-auto min-h-75">
            <table className="admin-table w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-(--admin-border-subtle)">
                  <th className="px-4 py-3 text-[11px] text-(--admin-text3) uppercase tracking-wider font-semibold">Empresa Critica</th>
                  <th className="text-right px-4 py-3 text-[11px] text-(--admin-text3) uppercase tracking-wider font-semibold">Fallos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-(--admin-border-subtle)">
                {data.empresasAlerta.map((a) => (
                  <tr key={a.nitId} className="hover:bg-red-50/40 transition-colors text-[13px]">
                    <td className="px-4 py-3">
                      <div className="font-bold text-red-900 truncate max-w-50">{a.nombre}</div>
                      <div className="text-[11px] text-red-600/70 truncate max-w-50">NIT: {a.nitId}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-[14px] font-black text-red-600">{a.indicadoresFallando}</div>
                      <div className="text-[10px] text-red-500/60 font-medium uppercase">Indicadores</div>
                    </td>
                  </tr>
                ))}
                {data.empresasAlerta.length === 0 && (
                  <tr>
                    <td colSpan={2} className="p-12 text-center text-green-600">
                      <Activity className="w-8 h-8 mx-auto mb-2 opacity-20" />
                      ¡Excelente! No hay empresas con alertas críticas
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── IndicadoresTab ──────────────────────────────────────

function IndicadoresTab() {
  const { data: indicadores, isLoading, refetch } = useIndicadoresBaseSysadmin();
  const { crear, isLoading: creating } = useCrearIndicadorBase();
  const { eliminar, isLoading: deleting } = useEliminarIndicadorBase();

  const [showForm, setShowForm] = useState(false);
  const [editingIndicador, setEditingIndicador] = useState<IndicadorBaseSysadmin | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const openCreate = useCallback(() => {
    setEditingIndicador(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowForm(true);
  }, []);

  const openEdit = useCallback((ind: IndicadorBaseSysadmin) => {
    setEditingIndicador(ind);
    setForm({
      nombre: ind.nombre,
      formula: ind.formula,
      frecuencia: ind.frecuencia,
      fuenteDatos: ind.fuenteDatos,
      meta: String(ind.meta),
      tipoResultado: ind.tipoResultado,
      variables:
        ind.variablesBase.length > 0
          ? ind.variablesBase.map((v) => ({ nombre: v.nombre, orden: v.orden ?? 1 }))
          : [{ nombre: '', orden: 1 }],
    });
    setFormError(null);
    setShowForm(true);
  }, []);

  const closeForm = useCallback(() => {
    setShowForm(false);
    setEditingIndicador(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!form.nombre.trim() || !form.formula.trim()) {
      setFormError('Nombre y fórmula son requeridos.');
      return;
    }
    const metaNum = parseFloat(form.meta);
    if (isNaN(metaNum)) {
      setFormError('La meta debe ser un número válido.');
      return;
    }

    const payload = {
      nombre: form.nombre.trim(),
      formula: form.formula.trim(),
      frecuencia: form.frecuencia,
      fuenteDatos: form.fuenteDatos.trim(),
      meta: metaNum,
      tipoResultado: form.tipoResultado,
      variables: form.variables.filter((v) => v.nombre.trim()).map((v) => ({ nombre: v.nombre.trim(), orden: v.orden })),
    };

    setSaving(true);
    setFormError(null);
    try {
      if (editingIndicador) {
        await sysadminService.actualizarIndicadorBase(editingIndicador.id, payload);
      } else {
        const result = await crear(payload);
        if (!result.success) {
          setFormError(result.error ?? 'Error al crear indicador.');
          return;
        }
      }
      await refetch();
      closeForm();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Error al guardar indicador.');
    } finally {
      setSaving(false);
    }
  }, [form, editingIndicador, crear, refetch, closeForm]);

  const handleDelete = useCallback(
    async (id: number) => {
      const result = await eliminar(id);
      if (result.success) {
        await refetch();
        setConfirmDelete(null);
      }
    },
    [eliminar, refetch]
  );

  const addVariable = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      variables: [...prev.variables, { nombre: '', orden: prev.variables.length + 1 }],
    }));
  }, []);

  const removeVariable = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index).map((v, i) => ({ ...v, orden: i + 1 })),
    }));
  }, []);

  const updateVariable = useCallback((index: number, nombre: string) => {
    setForm((prev) => {
      const updated = [...prev.variables];
      updated[index] = { ...updated[index], nombre };
      return { ...prev, variables: updated };
    });
  }, []);

  return (
    <div>
      {/* Inline form */}
      {showForm && (
        <div className="admin-card mb-4 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[14px] font-semibold text-(--admin-text)">{editingIndicador ? 'Editar indicador' : 'Nuevo indicador'}</h3>
            <button type="button" onClick={closeForm} className="text-(--admin-text3) hover:text-(--admin-text) transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-[11px] font-medium text-(--admin-text3) uppercase tracking-wider mb-1">Nombre</label>
              <input
                className={inputCls}
                value={form.nombre}
                onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
                placeholder="Nombre del indicador"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-(--admin-text3) uppercase tracking-wider mb-1">Fórmula</label>
              <input
                className={inputCls}
                value={form.formula}
                onChange={(e) => setForm((p) => ({ ...p, formula: e.target.value }))}
                placeholder="Ej: (A / B) * 100"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-(--admin-text3) uppercase tracking-wider mb-1">Frecuencia</label>
              <select
                className={inputCls}
                value={form.frecuencia}
                onChange={(e) => setForm((p) => ({ ...p, frecuencia: e.target.value as FormState['frecuencia'] }))}
              >
                <option value="MENSUAL">Mensual</option>
                <option value="TRIMESTRAL">Trimestral</option>
                <option value="SEMESTRAL">Semestral</option>
                <option value="ANUAL">Anual</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-(--admin-text3) uppercase tracking-wider mb-1">Fuente de datos</label>
              <input
                className={inputCls}
                value={form.fuenteDatos}
                onChange={(e) => setForm((p) => ({ ...p, fuenteDatos: e.target.value }))}
                placeholder="Fuente de datos"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-(--admin-text3) uppercase tracking-wider mb-1">Meta</label>
              <input
                type="number"
                className={inputCls}
                value={form.meta}
                onChange={(e) => setForm((p) => ({ ...p, meta: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-(--admin-text3) uppercase tracking-wider mb-1">Tipo resultado</label>
              <select
                className={inputCls}
                value={form.tipoResultado}
                onChange={(e) => setForm((p) => ({ ...p, tipoResultado: e.target.value as FormState['tipoResultado'] }))}
              >
                <option value="PORCENTAJE">Porcentaje (%)</option>
                <option value="NUMERO">Número</option>
                <option value="MONEDA">Moneda</option>
              </select>
            </div>
          </div>

          {/* Variables */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-medium text-(--admin-text3) uppercase tracking-wider">Variables</label>
              <button type="button" onClick={addVariable} className={btnSecondary}>
                <Plus className="w-3 h-3 inline mr-1" />
                Agregar variable
              </button>
            </div>
            <div className="space-y-2">
              {form.variables.map((v, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[11px] text-(--admin-text3) w-6 text-center">{v.orden}</span>
                  <input
                    className={inputCls}
                    value={v.nombre}
                    onChange={(e) => updateVariable(i, e.target.value)}
                    placeholder={`Variable ${v.orden}`}
                  />
                  {form.variables.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeVariable(i)}
                      className="text-red-400 hover:text-red-600 transition-colors shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {formError && <p className="text-[12px] text-red-600 mb-3">{formError}</p>}

          <div className="flex items-center gap-2 justify-end">
            <button type="button" onClick={closeForm} className={btnSecondary}>
              Cancelar
            </button>
            <button type="button" onClick={handleSubmit} disabled={saving || creating} className={btnPrimary}>
              {saving || creating ? 'Guardando...' : editingIndicador ? 'Guardar cambios' : 'Crear indicador'}
            </button>
          </div>
        </div>
      )}

      {/* Table card */}
      <div className="admin-card">
        <div className="admin-card-header">
          <div className="flex items-center gap-2 flex-1">
            <Activity className="w-4 h-4 text-(--admin-text3)" />
            <span className="text-[13px] font-semibold text-(--admin-text)">Indicadores base</span>
            {indicadores && <span className="text-[11px] text-(--admin-text3)">({indicadores.length})</span>}
          </div>
          {!showForm && (
            <button type="button" onClick={openCreate} className={btnPrimary}>
              <Plus className="w-3.5 h-3.5 inline mr-1" />
              Nuevo indicador
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <LoadingContainer text="Cargando indicadores..." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Fórmula</th>
                  <th>Frecuencia</th>
                  <th>Meta</th>
                  <th>Tipo</th>
                  <th>Variables</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {indicadores?.map((ind) => (
                  <Fragment key={ind.id}>
                    <tr className="hover:bg-(--admin-surface2) transition-colors">
                      <td className="font-medium text-(--admin-text)">{ind.nombre}</td>
                      <td className="text-(--admin-text2) font-mono text-[12px]">{ind.formula}</td>
                      <td>
                        <span className="text-[11px] font-medium text-(--admin-text2) bg-(--admin-surface2) px-2 py-0.5 rounded">
                          {formatFrecuencia(ind.frecuencia)}
                        </span>
                      </td>
                      <td className="text-(--admin-text2)">{formatValor(ind.meta, ind.tipoResultado)}</td>
                      <td>
                        <span className="text-[11px] font-medium text-(--admin-text2)">{ind.tipoResultado}</span>
                      </td>
                      <td className="text-(--admin-text3) text-[12px]">
                        {ind.variablesBase.length > 0 ? ind.variablesBase.map((v) => v.nombre).join(', ') : '—'}
                      </td>
                      <td>
                        <div className="flex items-center gap-1 justify-end">
                          <button type="button" onClick={() => openEdit(ind)} className={btnSecondary} title="Editar">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button type="button" onClick={() => setConfirmDelete(ind.id)} className={btnDanger} title="Eliminar">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {confirmDelete === ind.id && (
                      <tr key={`confirm-${ind.id}`} className="bg-red-50/60">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                            <span className="text-[13px] text-red-800 flex-1">
                              ¿Eliminar <strong>{ind.nombre}</strong>? Esta acción no se puede deshacer.
                            </span>
                            <button type="button" onClick={() => setConfirmDelete(null)} className={btnSecondary}>
                              Cancelar
                            </button>
                            <button type="button" onClick={() => handleDelete(ind.id)} disabled={deleting} className={btnDanger}>
                              {deleting ? 'Eliminando...' : 'Confirmar'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {(!indicadores || indicadores.length === 0) && (
                  <tr>
                    <td colSpan={7} className="p-12 text-center text-(--admin-text3)">
                      No hay indicadores base configurados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── EmpresaIndicadoresPanel ─────────────────────────────

function renderUltimaRevision(ind: IndicadorEmpresa) {
  const { ultimaEvaluacion, frecuencia } = ind;
  if (!ultimaEvaluacion) {
    return <span className="text-red-500 font-semibold text-[11px] animate-pulse">Sin evaluar</span>;
  }

  const date = new Date(ultimaEvaluacion);
  const diffTime = Date.now() - date.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  const limitDaysMap: Record<string, number> = {
    MENSUAL: 30,
    TRIMESTRAL: 90,
    SEMESTRAL: 180,
    ANUAL: 365,
  };
  const limit = limitDaysMap[frecuencia] || 30;
  const remaining = limit - diffDays;

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[12px] text-(--admin-text2)">
        {date.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' })}
      </span>
      <span className="text-[10px] text-(--admin-text3)">
        {diffDays === 0 ? 'Hoy' : `Hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`}
      </span>
      {remaining <= 0 ? (
        <span className="text-red-500 font-semibold text-[10px] uppercase">
          Vencido hace {Math.abs(remaining)} día{Math.abs(remaining) !== 1 ? 's' : ''}
        </span>
      ) : (
        <span className="text-green-600 text-[10px]">
          Vence en {remaining} día{remaining !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}

export function EmpresaIndicadoresPanel({ nit, readOnly = false }: { nit: string; readOnly?: boolean }) {
  const { data: indicadores, isLoading, refetch } = useIndicadoresByEmpresa(nit);
  const { data: baseIndicadores } = useIndicadoresBaseSysadmin();
  const { asignar, isLoading: assigning } = useAsignarIndicador(nit);
  const { eliminar, isLoading: deleting } = useEliminarIndicadorEmpresa(nit);
  const { data: historial } = useHistorialIndicadores(nit);
  const navigate = useNavigate();

  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [hoveredRect, setHoveredRect] = useState<DOMRect | null>(null);

  // Filters state
  const [filterEstado, setFilterEstado] = useState<string>('TODOS');
  const [filterFrecuencia, setFilterFrecuencia] = useState<string>('TODAS');

  const unassignedBaseIndicadores = useMemo(() => {
    if (!baseIndicadores) return [];
    if (!indicadores) return baseIndicadores;
    return baseIndicadores.filter((b) => !indicadores.some((i) => i.nombre.toLowerCase().trim() === b.nombre.toLowerCase().trim()));
  }, [baseIndicadores, indicadores]);

  const filteredIndicadores = useMemo(() => {
    if (!indicadores) return [];
    let result = [...indicadores];

    // Filter by compliance state
    if (filterEstado !== 'TODOS') {
      if (filterEstado === 'SIN_EVALUAR') {
        result = result.filter((ind) => !ind.ultimaEvaluacion);
      } else if (filterEstado === 'EVALUADO') {
        result = result.filter((ind) => !!ind.ultimaEvaluacion);
      } else if (filterEstado === 'CUMPLE') {
        result = result.filter((ind) => !!ind.ultimaEvaluacion && ind.estado === 'CUMPLE');
      } else if (filterEstado === 'NO_CUMPLE') {
        result = result.filter((ind) => !!ind.ultimaEvaluacion && ind.estado === 'NO_CUMPLE');
      }
    }

    // Filter by frequency
    if (filterFrecuencia !== 'TODAS') {
      result = result.filter((ind) => ind.frecuencia === filterFrecuencia);
    }

    return result;
  }, [indicadores, filterEstado, filterFrecuencia]);

  const activeHoveredInd = useMemo(() => {
    if (hoveredId === null || !indicadores) return null;
    return indicadores.find((ind) => ind.id === hoveredId) || null;
  }, [hoveredId, indicadores]);

  const getTooltipStyle = (rect: DOMRect) => {
    const tooltipWidth = 320;
    const tooltipHeight = 240;
    let left = rect.left;
    let top = rect.bottom + 6;

    // Check right screen boundary
    if (left + tooltipWidth > window.innerWidth - 16) {
      left = window.innerWidth - tooltipWidth - 16;
    }
    // Check left screen boundary
    if (left < 16) {
      left = 16;
    }

    // Check bottom screen boundary
    if (top + tooltipHeight > window.innerHeight - 16) {
      // Place it above the element
      top = rect.top - tooltipHeight - 6;
      // If it goes above the top boundary of screen, fallback to bottom placement
      if (top < 16) {
        top = rect.bottom + 6;
      }
    }

    return {
      position: 'fixed' as const,
      top: `${top}px`,
      left: `${left}px`,
      width: `${tooltipWidth}px`,
      zIndex: 9999,
      pointerEvents: 'none' as const,
      backdropFilter: 'blur(8px)',
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
    };
  };

  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [selectedBaseId, setSelectedBaseId] = useState<string>('');
  const [assignError, setAssignError] = useState<string | null>(null);
  const [showAssignRow, setShowAssignRow] = useState(false);

  const handleAsignar = useCallback(async () => {
    if (!selectedBaseId) return;
    setAssignError(null);
    const result = await asignar(parseInt(selectedBaseId, 10));
    if (result.success) {
      await refetch();
      setSelectedBaseId('');
      setShowAssignRow(false);
    } else {
      setAssignError(result.error ?? 'Error al asignar indicador.');
    }
  }, [selectedBaseId, asignar, refetch]);

  const handleEliminar = useCallback(
    async (id: number) => {
      const result = await eliminar(id);
      if (result.success) {
        await refetch();
        setConfirmDelete(null);
      }
    },
    [eliminar, refetch]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingContainer text="Cargando indicadores..." />
      </div>
    );
  }

  return (
    <div>
      {/* Header actions */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[13px] font-semibold text-(--admin-text)">Indicadores asignados ({indicadores?.length ?? 0})</span>
        {!readOnly && !showAssignRow && unassignedBaseIndicadores.length > 0 && (
          <button type="button" onClick={() => setShowAssignRow(true)} className={btnPrimary}>
            <Plus className="w-3.5 h-3.5 inline mr-1" />
            Asignar indicador
          </button>
        )}
      </div>

      {/* Assign row */}
      {!readOnly && showAssignRow && (
        <div className="flex items-center gap-2 mb-3 p-3 bg-(--admin-surface2) rounded-lg border border-(--admin-border)">
          <select className={`${inputCls} flex-1`} value={selectedBaseId} onChange={(e) => setSelectedBaseId(e.target.value)}>
            <option value="">Seleccionar indicador base...</option>
            {unassignedBaseIndicadores.map((b) => (
              <option key={b.id} value={b.id}>
                {b.nombre}
              </option>
            ))}
          </select>
          <button type="button" onClick={handleAsignar} disabled={!selectedBaseId || assigning} className={btnPrimary}>
            {assigning ? 'Asignando...' : 'Asignar'}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowAssignRow(false);
              setSelectedBaseId('');
              setAssignError(null);
            }}
            className={btnSecondary}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {assignError && <p className="text-[12px] text-red-600 mb-3">{assignError}</p>}

      {/* Filter Bar for Admin */}
      <div className="p-3 mb-3 border border-gray-100 rounded-lg flex flex-wrap items-center gap-4 bg-[#fcfbf9]">
        <div className="flex items-center gap-2">
          <label htmlFor="filter-estado-admin" className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Estado:
          </label>
          <select
            id="filter-estado-admin"
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
            className="admin-select py-1! text-[11px] h-7 border-(--admin-border) rounded-lg bg-white"
            style={{ minHeight: '28px' }}
          >
            <option value="TODOS">Todos</option>
            <option value="SIN_EVALUAR">Sin evaluar</option>
            <option value="EVALUADO">Evaluados</option>
            <option value="CUMPLE">Cumple</option>
            <option value="NO_CUMPLE">No cumple</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="filter-frecuencia-admin" className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Frecuencia:
          </label>
          <select
            id="filter-frecuencia-admin"
            value={filterFrecuencia}
            onChange={(e) => setFilterFrecuencia(e.target.value)}
            className="admin-select py-1! text-[11px] h-7 border-(--admin-border) rounded-lg bg-white"
            style={{ minHeight: '28px' }}
          >
            <option value="TODAS">Todas</option>
            <option value="MENSUAL">Mensual</option>
            <option value="TRIMESTRAL">Trimestral</option>
            <option value="SEMESTRAL">Semestral</option>
            <option value="ANUAL">Anual</option>
          </select>
        </div>

        <div className="text-[11px] text-gray-400 sm:ml-auto font-medium">
          Mostrando {filteredIndicadores.length} de {indicadores?.length ?? 0} indicadores
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="admin-table w-full">
          <thead>
            <tr>
              <th className="text-left py-3 px-4">Nombre</th>
              <th className="text-left py-3 px-4">Frecuencia</th>
              <th className="text-left py-3 px-4">Estado</th>
              <th className="text-left py-3 px-4">Resultado</th>
              <th className="text-left py-3 px-4">Meta</th>
              <th className="text-left py-3 px-4">Última Revisión</th>
              <th className="text-right py-3 px-4">Acción</th>
            </tr>
          </thead>
          <tbody>
            {filteredIndicadores.map((ind) => (
              <Fragment key={ind.id}>
                <tr className="hover:bg-gray-50/60 transition-colors border-b border-gray-100 last:border-0">
                  <td
                    className="py-3 px-4 font-medium text-gray-800 relative align-middle max-w-xs cursor-help"
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setHoveredRect(rect);
                      setHoveredId(ind.id);
                    }}
                    onMouseLeave={() => {
                      setHoveredId(null);
                      setHoveredRect(null);
                    }}
                  >
                    <span className="border-b border-dotted border-gray-400 pb-0.5 hover:text-blue-600 transition-colors">
                      {ind.nombre}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-600 text-[13px] align-middle">{formatFrecuencia(ind.frecuencia)}</td>
                  <td className="py-3 px-4 align-middle">
                    {!ind.ultimaEvaluacion ? (
                      <span className="text-[12px] text-gray-400 italic">Sin evaluar</span>
                    ) : (
                      <span className={`admin-chip ${ind.estado === 'CUMPLE' ? 'admin-chip-green' : 'admin-chip-red'}`}>
                        {ind.estado === 'CUMPLE' ? 'Cumple' : 'No cumple'}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-gray-700 font-medium align-middle">{formatValor(ind.resultado, ind.tipoResultado)}</td>
                  <td className="py-3 px-4 text-gray-500 align-middle">{formatValor(ind.meta, ind.tipoResultado)}</td>
                  <td className="py-3 px-4 align-middle">{renderUltimaRevision(ind)}</td>
                  <td className="py-3 px-4 text-right align-middle">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button
                        type="button"
                        onClick={() => navigate(`/empresa/${nit}/indicador/${ind.id}?from=admin`)}
                        className="admin-btn primary sm hover:scale-[1.02] active:scale-[0.98] transition-all"
                      >
                        Ver detalle
                      </button>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(ind.id)}
                          className={btnDanger}
                          title="Eliminar"
                          style={{ padding: '4px 8px' }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {!readOnly && confirmDelete === ind.id && (
                  <tr className="bg-red-50/60">
                    <td colSpan={7} className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                        <span className="text-[13px] text-red-800 flex-1">
                          ¿Eliminar <strong>{ind.nombre}</strong> de esta empresa?
                        </span>
                        <button type="button" onClick={() => setConfirmDelete(null)} className={btnSecondary}>
                          Cancelar
                        </button>
                        <button type="button" onClick={() => handleEliminar(ind.id)} disabled={deleting} className={btnDanger}>
                          {deleting ? 'Eliminando...' : 'Confirmar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {!indicadores || indicadores.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-(--admin-text3)">
                  No hay indicadores asignados a esta empresa
                </td>
              </tr>
            ) : filteredIndicadores.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-(--admin-text3)">
                  Ningún indicador coincide con los filtros aplicados
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Floating Hover detail card outside overflow container to prevent clipping */}
      {activeHoveredInd && hoveredRect && (
        <div
          className="fixed p-4 bg-white border border-gray-200 rounded-xl shadow-xl text-xs space-y-2.5 text-left pointer-events-none transition-all duration-200"
          style={getTooltipStyle(hoveredRect)}
        >
          <p className="font-bold text-gray-800 text-[13px] border-b border-gray-100 pb-1">{activeHoveredInd.nombre}</p>

          <div>
            <span className="font-semibold text-gray-400 uppercase tracking-wider text-[9px] block mb-0.5">Fórmula Teórica</span>
            <p className="text-gray-700 bg-gray-50 p-2 rounded-lg font-mono border border-gray-100 leading-relaxed text-[11px]">
              {activeHoveredInd.formula}
            </p>
          </div>

          {activeHoveredInd.fuenteDatos && (
            <div>
              <span className="font-semibold text-gray-400 uppercase tracking-wider text-[9px] block mb-0.5">Source of Data</span>
              <p className="text-gray-600 leading-relaxed">{activeHoveredInd.fuenteDatos}</p>
            </div>
          )}

          <div className="flex gap-6 pt-2 border-t border-gray-100">
            <div>
              <span className="font-semibold text-gray-400 uppercase tracking-wider text-[9px] block">Frecuencia</span>
              <span className="text-gray-700 font-semibold">{formatFrecuencia(activeHoveredInd.frecuencia)}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-400 uppercase tracking-wider text-[9px] block">Meta</span>
              <span className="text-gray-700 font-semibold">{formatValor(activeHoveredInd.meta, activeHoveredInd.tipoResultado)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AsignacionesTab ─────────────────────────────────────

export function CompanyIndicatorsCharts({ nit }: { nit: string }) {
  const { data: indicadores, isLoading: loadingInd } = useIndicadoresByEmpresa(nit);
  const { data: historial, isLoading: loadingHist, error } = useHistorialIndicadores(nit);

  const [selectedYear, setSelectedYear] = useState<string>('todos');
  const [selectedMonth, setSelectedMonth] = useState<string>('todos');

  // Compute available years and months from history
  const { years, months } = useMemo(() => {
    if (!historial || historial.length === 0) return { years: [], months: [] };
    const ySet = new Set<number>();
    const mSet = new Set<number>();
    historial.forEach((h) => {
      ySet.add(h.anio);
      mSet.add(h.mes);
    });
    return {
      years: Array.from(ySet).sort((a, b) => b - a),
      months: Array.from(mSet).sort((a, b) => a - b),
    };
  }, [historial]);

  if (loadingInd || loadingHist) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="flex items-center justify-center h-48 bg-(--admin-surface2) rounded-xl border border-(--admin-border) animate-pulse">
          <div className="w-6 h-6 rounded-full border-2 border-(--admin-blue) border-t-transparent animate-spin" />
        </div>
        <div className="flex items-center justify-center h-48 bg-(--admin-surface2) rounded-xl border border-(--admin-border) animate-pulse">
          <div className="w-6 h-6 rounded-full border-2 border-(--admin-blue) border-t-transparent animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !indicadores || indicadores.length === 0) return null;

  const getMonthName = (monthNum: number) => {
    const monthsName = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return monthsName[monthNum - 1] || '';
  };

  return (
    <div className="mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h4
          style={{ fontFamily: 'Manrope, system-ui, sans-serif', fontWeight: 600 }}
          className="text-[12px] text-(--admin-text2) uppercase tracking-wider"
        >
          Evolución por indicador
        </h4>
        <div className="flex items-center gap-2">
          {/* Year Filter */}
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="admin-select py-1! text-[11px] h-7 border-(--admin-border)"
          >
            <option value="todos">Todos los años</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          {/* Month Filter */}
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="admin-select py-1! text-[11px] h-7 border-(--admin-border)"
          >
            <option value="todos">Todos los meses</option>
            {months.map((m) => (
              <option key={m} value={m}>
                {getMonthName(m)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {indicadores.map((ind) => {
          let indHist = (historial || []).filter((h) => h.indicadorEmpresaId === ind.id);

          if (selectedYear !== 'todos') {
            indHist = indHist.filter((h) => h.anio.toString() === selectedYear);
          }
          if (selectedMonth !== 'todos') {
            indHist = indHist.filter((h) => h.mes.toString() === selectedMonth);
          }

          // Group by month and year, selecting the most recent entry for each month
          const monthlyLatestMap = new Map<string, any>();
          indHist.forEach((h) => {
            const key = `${h.mes}-${h.anio}`;
            const existing = monthlyLatestMap.get(key);
            if (!existing || new Date(h.fechaRegistro).getTime() > new Date(existing.fechaRegistro).getTime()) {
              monthlyLatestMap.set(key, h);
            }
          });

          const chartData = Array.from(monthlyLatestMap.values())
            .sort((a, b) => (a.anio !== b.anio ? a.anio - b.anio : a.mes - b.mes))
            .map((h) => ({
              name: `${getMonthName(h.mes)} ${h.anio}`,
              meta: h.valorMeta,
              actual: h.valorActual,
            }));

          if (chartData.length === 0) {
            return (
              <div
                key={ind.id}
                className="p-4 bg-(--admin-surface2) rounded-xl border border-(--admin-border) flex flex-col justify-center items-center h-48 text-center"
              >
                <div className="text-[13px] font-semibold text-(--admin-text) mb-1">{ind.nombre}</div>
                <p className="text-[11px] text-(--admin-text3)">Sin datos históricos para graficar.</p>
              </div>
            );
          }

          return (
            <div key={ind.id} className="p-4 bg-(--admin-surface2) rounded-xl border border-(--admin-border)">
              <div className="flex items-center justify-between mb-3 min-w-0">
                <div
                  style={{ fontFamily: 'Manrope, system-ui, sans-serif', fontWeight: 700 }}
                  className="text-[13px] text-(--admin-text) truncate pr-2"
                  title={ind.nombre}
                >
                  {ind.nombre}
                </div>
                <span className="text-[11px] font-medium text-(--admin-text2) bg-(--admin-surface) px-2 py-0.5 rounded border border-(--admin-border) shrink-0">
                  Meta: {formatValor(ind.meta, ind.tipoResultado)}
                </span>
              </div>
              <div className="h-36 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                    <XAxis dataKey="name" stroke="var(--admin-text3)" style={{ fontSize: '9px' }} />
                    <YAxis
                      stroke="var(--admin-text3)"
                      style={{ fontSize: '9px' }}
                      tickFormatter={(val) => formatValor(val, ind.tipoResultado, true)}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'var(--admin-surface)',
                        border: '1px solid var(--admin-border)',
                        borderRadius: 'var(--admin-r)',
                        fontSize: '11px',
                        padding: '6px 10px',
                      }}
                      formatter={(value, name) => [
                        formatValor(Number(value), ind.tipoResultado),
                        name === 'actual' ? 'Valor actual' : 'Meta',
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="meta"
                      stroke="var(--admin-border2)"
                      strokeWidth={1.5}
                      strokeDasharray="4 4"
                      name="Meta"
                      dot={{ fill: 'var(--admin-border2)', r: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="actual"
                      stroke="var(--admin-blue)"
                      strokeWidth={2}
                      name="Valor actual"
                      dot={{ fill: 'var(--admin-blue)', r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function renderAsignacionRow(e: any, navigate: any) {
  const cumple = e.cantidadCumplen ?? 0;
  const noCumple = e.cantidadNoCumplen ?? 0;
  const pendientes = e.cantidadPendientes ?? 0;

  return (
    <tr
      key={e.nitId}
      className="group hover:bg-[#faf9f7]! clickable text-[13px]"
      onClick={() => navigate(`/indicadores/asignaciones/${e.nitId}`)}
    >
      <td className="font-medium text-(--admin-text) max-w-64 truncate">{e.nombre}</td>
      <td className="font-mono text-[11px] text-(--admin-text3) whitespace-nowrap">{e.nitId}</td>
      <td className="text-(--admin-text2) whitespace-nowrap text-[12px]">{e.asesor?.nombre ?? '—'}</td>
      <td className="whitespace-nowrap">
        <span className="admin-chip bg-(--admin-surface2) text-(--admin-text2) text-[10px] border-(--admin-border)">
          {e.combinacionNtc?.nombre ?? '—'}
        </span>
      </td>
      <td className="whitespace-nowrap">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="admin-chip bg-(--admin-green-bg) text-(--admin-green) text-[10.5px] font-semibold border-transparent shrink-0">
            {cumple} cumple{cumple !== 1 ? 'n' : ''}
          </span>
          <span className="admin-chip bg-(--admin-red-bg) text-(--admin-red) text-[10.5px] font-semibold border-transparent shrink-0">
            {noCumple} no cumple{noCumple !== 1 ? 'n' : ''}
          </span>
          {pendientes > 0 ? (
            <span className="admin-chip bg-(--admin-amber-bg) text-(--admin-amber) text-[10.5px] font-semibold border-transparent shrink-0 animate-pulse">
              {pendientes} pendiente{pendientes !== 1 ? 's' : ''}
            </span>
          ) : (
            <span className="admin-chip bg-gray-100 text-gray-500 text-[10.5px] font-semibold border-transparent shrink-0">Al día</span>
          )}
        </div>
      </td>
      <td className="text-right whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
        <button
          type="button"
          className="admin-btn text-[11px] py-1 px-3 bg-white border-(--admin-border) opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => navigate(`/indicadores/asignaciones/${e.nitId}`)}
        >
          Gestionar
        </button>
      </td>
    </tr>
  );
}

function AsignacionesTab() {
  const { nit } = useParams<{ nit?: string }>();
  const navigate = useNavigate();
  const { empresas, isLoading: loadingEmpresas } = useEmpresasAdmin();
  const { asesores } = useDashboardAsesores();

  const selectedNit = nit || null;

  // Search & Filter State
  const [search, setSearch] = useState('');
  const [ntcFilter, setNtcFilter] = useState('');
  const [asesorFilter, setAsesorFilter] = useState('');
  const [groupBy, setGroupBy] = useState<'none' | 'asesor' | 'ntc'>('none');
  const [sortBy, setSortBy] = useState<'nombre' | 'cantidad' | 'cumple' | 'pendientes'>('nombre');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const uniqueNTCs = useMemo(() => {
    const s = new Set(empresas.map((e) => e.combinacionNtc?.nombre).filter(Boolean));
    return Array.from(s).sort();
  }, [empresas]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return empresas.filter((e) => {
      const matchSearch = !q || e.nombre?.toLowerCase().includes(q) || e.nitId?.toLowerCase().includes(q);
      const matchNTC = !ntcFilter || e.combinacionNtc?.nombre === ntcFilter;
      const matchAsesor = !asesorFilter || e.asesorId?.toString() === asesorFilter;
      return matchSearch && matchNTC && matchAsesor;
    });
  }, [empresas, search, ntcFilter, asesorFilter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (sortBy === 'cantidad') {
        return (b.cantidadIndicadores ?? 0) - (a.cantidadIndicadores ?? 0);
      }
      if (sortBy === 'cumple') {
        return (b.cantidadCumplen ?? 0) - (a.cantidadCumplen ?? 0);
      }
      if (sortBy === 'pendientes') {
        return (b.cantidadPendientes ?? 0) - (a.cantidadPendientes ?? 0);
      }
      return (a.nombre ?? '').localeCompare(b.nombre ?? '');
    });
  }, [filtered, sortBy]);

  const groups = useMemo(() => {
    if (groupBy === 'none') return null;
    const map = new Map<string, typeof sorted>();
    sorted.forEach((e) => {
      let key = 'Sin clasificar';
      if (groupBy === 'asesor') key = e.asesor?.nombre || 'Sin Asesor';
      if (groupBy === 'ntc') key = e.combinacionNtc?.nombre || 'Sin NTC';

      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [sorted, groupBy]);

  // Reset page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, ntcFilter, asesorFilter, groupBy, sortBy]);

  const paginated = useMemo(() => {
    if (groupBy !== 'none') return sorted;
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, groupBy, currentPage, pageSize]);

  const totalPages = Math.ceil(sorted.length / pageSize);

  if (loadingEmpresas) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingContainer text="Cargando empresas..." />
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      {!selectedNit ? (
        <div className="w-full max-w-full space-y-4">
          {/* Advanced Filters */}
          <div className="bg-(--admin-surface) border border-(--admin-border) rounded-xl p-4 mb-4 shadow-sm w-full max-w-full overflow-x-hidden">
            <div className="flex flex-wrap gap-4 items-end">
              {/* Search */}
              <div className="flex-1 min-w-60">
                <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Búsqueda</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-(--admin-text3)" />
                  <input
                    type="text"
                    placeholder="Buscar empresa por nombre o NIT..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="admin-search-input w-full pl-9"
                  />
                </div>
              </div>

              {/* NTC Filter */}
              <div className="w-44">
                <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Norma NTC</label>
                <select value={ntcFilter} onChange={(e) => setNtcFilter(e.target.value)} className="admin-select w-full">
                  <option value="">Todas</option>
                  {uniqueNTCs.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>

              {/* Asesor Filter */}
              <div className="w-44">
                <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Asesor</label>
                <select value={asesorFilter} onChange={(e) => setAsesorFilter(e.target.value)} className="admin-select w-full">
                  <option value="">Todos</option>
                  {[...(asesores || [])]
                    .sort((a, b) => a.nombre.localeCompare(b.nombre))
                    .map((a) => (
                      <option key={a.asesorId} value={a.asesorId}>
                        {a.nombre}
                      </option>
                    ))}
                </select>
              </div>

              {/* Sort & Group */}
              <div className="flex gap-2 border-l border-(--admin-border) pl-4 ml-auto">
                <div>
                  <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Agrupar por</label>
                  <div className="flex bg-(--admin-surface2) p-0.5 rounded-lg border border-(--admin-border)">
                    <button
                      type="button"
                      onClick={() => setGroupBy('none')}
                      className={`px-3 py-1 text-[11px] rounded-md transition-all ${groupBy === 'none' ? 'bg-white shadow-sm font-semibold text-(--admin-text)' : 'text-(--admin-text3) hover:text-(--admin-text2)'}`}
                    >
                      Nada
                    </button>
                    <button
                      type="button"
                      onClick={() => setGroupBy('asesor')}
                      className={`px-3 py-1 text-[11px] rounded-md transition-all ${groupBy === 'asesor' ? 'bg-white shadow-sm font-semibold text-(--admin-text)' : 'text-(--admin-text3) hover:text-(--admin-text2)'}`}
                    >
                      Asesor
                    </button>
                    <button
                      type="button"
                      onClick={() => setGroupBy('ntc')}
                      className={`px-3 py-1 text-[11px] rounded-md transition-all ${groupBy === 'ntc' ? 'bg-white shadow-sm font-semibold text-(--admin-text)' : 'text-(--admin-text3) hover:text-(--admin-text2)'}`}
                    >
                      Norma
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider mb-1.5 block">Ordenar por</label>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} className="admin-select">
                    <option value="nombre">Nombre (A-Z)</option>
                    <option value="cumple">Más Cumplen</option>
                    <option value="pendientes">Más Pendientes</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="admin-card w-full max-w-full overflow-x-hidden">
            <div className="admin-card-header flex items-center justify-between gap-3">
              <div className="text-[13px] font-semibold text-(--admin-text) flex items-center gap-2">
                Asignaciones por Empresa
                <span className="bg-(--admin-surface2) px-1.5 py-0.5 rounded text-(--admin-text3) font-normal text-[11px] border border-(--admin-border)">
                  {sorted.length}
                </span>
              </div>
            </div>
            <div className="w-full overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Empresa</th>
                    <th>NIT</th>
                    <th>Asesor</th>
                    <th>Norma NTC</th>
                    <th>Estado de Evaluación</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {groupBy === 'none'
                    ? paginated.map((e) => renderAsignacionRow(e, navigate))
                    : groups?.map(([groupName, groupData]) => (
                        <Fragment key={groupName}>
                          <tr className="bg-(--admin-bg) border-y border-(--admin-border)">
                            <td colSpan={6} className="py-2.5 px-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-[11px] font-bold text-(--admin-text2) uppercase tracking-widest">{groupName}</span>
                                  <span className="text-[10px] text-(--admin-text3) bg-white px-1.5 py-0.5 rounded border border-(--admin-border)">
                                    {groupData.length}
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>
                          {groupData.map((e) => renderAsignacionRow(e, navigate))}
                        </Fragment>
                      ))}
                  {sorted.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-12 text-(--admin-text3)">
                        <Building2 className="w-10 h-10 mx-auto mb-3 opacity-20" />
                        <div className="text-[14px] font-medium">No se encontraron empresas</div>
                        <div className="text-[12px] opacity-70">Intenta ajustar los filtros de búsqueda</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {groupBy === 'none' && totalPages > 1 && (
              <div className="flex items-center justify-between p-4 border-t border-(--admin-border) bg-(--admin-surface)">
                <div className="text-[12px] text-(--admin-text3)">
                  Mostrando <span className="font-semibold text-(--admin-text)">{(currentPage - 1) * pageSize + 1}</span> a{' '}
                  <span className="font-semibold text-(--admin-text)">{Math.min(currentPage * pageSize, sorted.length)}</span> de{' '}
                  <span className="font-semibold text-(--admin-text)">{sorted.length}</span> empresas
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    className={`${btnSecondary} py-1! px-2.5 disabled:opacity-50`}
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-[12px] text-(--admin-text2)">
                    Pág. {currentPage} de {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    className={`${btnSecondary} py-1! px-2.5 disabled:opacity-50`}
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="admin-select py-1! px-2! text-[11px] h-7 border-(--admin-border)"
                  >
                    <option value={5}>5 por pág.</option>
                    <option value={10}>10 por pág.</option>
                    <option value={20}>20 por pág.</option>
                    <option value={50}>50 por pág.</option>
                    <option value={100}>100 por pág.</option>
                    <option value={200}>200 por pág.</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Focused view: Selected company takes up full width, list is hidden */
        <div className="admin-card w-full max-w-full">
          <div className="admin-card-header flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-(--admin-text3)" />
              <span style={{ fontFamily: 'Manrope, system-ui, sans-serif', fontWeight: 600 }} className="text-[13px] text-(--admin-text)">
                {empresas.find((e) => e.nitId === selectedNit)?.nombre ?? selectedNit}
              </span>
            </div>
            <button
              type="button"
              onClick={() => navigate('/indicadores/asignaciones')}
              className="p-1 rounded-lg text-(--admin-text3) hover:text-(--admin-text) hover:bg-(--admin-surface2) transition-all"
              title="Volver a la lista de empresas"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4 space-y-6">
            <CompanyIndicatorsCharts nit={selectedNit} />
            <EmpresaIndicadoresPanel nit={selectedNit} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────

export function AdminIndicadoresPage() {
  const { tab, nit } = useParams<{ tab?: string; nit?: string }>();
  const navigate = useNavigate();
  const activeTab: TabId = (tab as TabId) ?? 'resumen';

  void Check;

  const setTab = (id: TabId) => navigate(`/indicadores/${id}`);

  return (
    <>
      {/* Tab bar */}
      <div className="flex gap-1 mb-4 border-b border-(--admin-border) pb-0">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
              activeTab === t.id
                ? 'border-(--admin-text) text-(--admin-text)'
                : 'border-transparent text-(--admin-text3) hover:text-(--admin-text2)'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'resumen' && <ResumenTab />}
      {activeTab === 'gestion' && <IndicadoresTab />}
      {activeTab === 'asignaciones' && <AsignacionesTab />}
    </>
  );
}
